import { useQueueClaimConflict } from '@/hooks/useQueueClaimConflict';
import { isErrorWithData } from '@/services/helpers';
import { useGetQueueByAccountIdAndTypeQuery, useUpdateQueueMutation } from '@/services/queues';
import { ReviewPackageActions } from '@/types/ReviewPackage';
import { Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { QueueRejectionReason, QueueStatus, QueueType } from 'types';

import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { DocumentRejectionModal } from '../DocumentRejectionModal/DocumentRejectionModal';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';

export const DesignDocumentsReview = () => {
  const { claims, user } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { id = '', action = ReviewPackageActions.review } = useParams<{
    id: string;
    action: ReviewPackageActions;
  }>();
  const { data: designDocsQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.loopFieldDesignDocuments,
  });
  const [updateQueue, { isLoading: isUpdatingQueue }] = useUpdateQueueMutation();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const allowsAction = useIsOrgReadOnly();
  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(designDocsQueue);

  const handleQueueUpdate = useCallback(
    async (status: QueueStatus, note?: string, reasons?: QueueRejectionReason[]) => {
      if (!designDocsQueue?.id) return;
      try {
        await updateQueue({
          id: designDocsQueue.id,
          data: {
            assigneeId: user?.sub,
            status,
            ...(reasons && {
              rejectionReasons: [
                {
                  reasons,
                  ...(note && { note }),
                },
              ],
            }),
          },
        }).unwrap();
        toast.success('Queue updated successfully');
      } catch (e) {
        if (isErrorWithData(e)) {
          toast.error(`Error updating queue: ${e.data.message}`);
        } else {
          toast.error('Error updating queue');
        }
      }
    },
    [designDocsQueue?.id, updateQueue, user?.sub],
  );

  const approve = useCallback(
    () => checkClaimAndProceed(() => handleQueueUpdate(QueueStatus.approved)),
    [checkClaimAndProceed, handleQueueUpdate],
  );

  const handleRejectClick = useCallback(() => checkClaimAndProceed(openModal), [checkClaimAndProceed, openModal]);

  const handleRejectModal = useCallback(
    async (values: { rejectionReason: string; notes: string | undefined }) => {
      await handleQueueUpdate(QueueStatus.rejected, values.notes, [values.rejectionReason as QueueRejectionReason]);
      closeModal();
    },
    [handleQueueUpdate, closeModal],
  );

  if (!designDocsQueue) return null;
  if (designDocsQueue.status !== QueueStatus.rejected && !isFinCoUser) return null;

  return (
    <>
      {claimConflictModal}
      <DocumentRejectionModal
        isModalOpen={isModalOpen}
        title="Design Documents"
        handleClose={closeModal}
        handleSubmit={handleRejectModal}
      />
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.loopFieldDesignDocuments} />
      <Box padding="md" margin="0 0 lg 0" borderColor="separator" borderWidth="xs" gap="sm">
        <h3>Design Documents Review</h3>
        <QueueBanner
          setIsNotesOpen={setIsNotesOpen}
          queueType={QueueType.loopFieldDesignDocuments}
          isFinCoUser={isFinCoUser}
          hideFlagCount
        />
        {action === ReviewPackageActions.review && designDocsQueue.status !== QueueStatus.approved && (
          <Box direction="row" childGap="xs" justifyContent="flex-end" alignItems="flex-end">
            <Button
              size="sm"
              variant="primary"
              isLoading={isUpdatingQueue}
              onClick={approve}
              disabled={!allowsAction}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="primary"
              tone="danger"
              isLoading={isUpdatingQueue}
              onClick={handleRejectClick}
              disabled={!allowsAction}
            >
              Reject
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};
